import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Checkbox,
  CircularProgress,
  Grid,
  Modal,
  Typography,
} from '@mui/material';
import {
  EditAddUserModal,
  UserModelGrid,
  UserModelValue,
  UserModalLabel,
  saveModalButtonGrid,
  UserModalHeading,
  EditAddUserModalLoader,
  validationError,
} from '../../styles';
import InputTextField from '../../components/atoms/input-field/inputTextField';
import { default as CustomButton } from '../../components/atoms/button';
import {
  isEmptyObject,
  keyMapper,
  validateRequiredInputField,
} from '../../utils';
import CustomSelect from '../../components/atoms/custom-select';

const customStyle = {
  menu: provided => ({ ...provided, zIndex: 9999 }),
};

//Add/Edit User Modal
const AddEditUserModal = props => {
  const {
    isUserModalOpen,
    onSaveUserModalClick,
    onCloseUserModalClick,
    addOrEdit = 'Add',
    siteListData = [],
    departmentListData = [],
    userDataForEdit = {},
    allusersEmail = [],
  } = props;
  const { t } = useTranslation();
  const firedRef = useRef(false);

  const [userData, setUserData] = useState({
    UserId: -1,
    FirstName: '',
    LastName: '',
    Email: '',
    SiteName: '',
    SiteID: '',
    Active: false,
    DepartmentName: '',
    DepartmentID: -1,
    DepartmentLead: false,
  });
  const [inputValidationMessage, setInputValidationMessage] = useState({
    FirstName: '',
    LastName: '',
    Email: '',
  });

  const hasNotValidationError =
    inputValidationMessage.FirstName === '' &&
    inputValidationMessage.LastName === '' &&
    inputValidationMessage.Email === '';

  //To setUserData only when userDataForEdit has value
  useEffect(() => {
    if (firedRef.current || isEmptyObject(userDataForEdit)) return;
    firedRef.current = true;
    setUserData({
      ...userDataForEdit,
      DepartmentLead: userDataForEdit.DepartmentLead === 'Yes' ? true : false,
      Active: userDataForEdit.Active.toString() === '1' ? true : false,
    });
  }, [userDataForEdit]);

  //options for Site drop-down
  const siteOption =
    siteListData && siteListData.length > 0
      ? siteListData.map(item => {
          return {
            label: item.SiteName,
            value: item.ID,
          };
        })
      : [];

  //options for Department drop-down
  const departmentOption =
    departmentListData && departmentListData.length > 0
      ? departmentListData.map(item => {
          return {
            label: item.Name,
            value: item.ID,
          };
        })
      : [];

  //To get the value for Site drop-down
  const siteValue =
    siteOption && siteOption.length > 0 && userData && userData.SiteID
      ? siteOption.filter(
          item => item.value.toString() === userData.SiteID.toString()
        )
      : '';

  //To get the value for Department drop-down
  const departmentValue =
    departmentOption &&
    departmentOption.length > 0 &&
    userData &&
    userData.DepartmentID
      ? departmentOption.filter(
          item => item.value.toString() === userData.DepartmentID.toString()
        )
      : '';

  //To handle the onInput functionality of text fields
  const handleOnTextChange = (event, key) => {
    const inputValue = event.target.value.trim();
    setUserData({
      ...userData,
      [key]: inputValue,
    });
    const allUsersEmailID =
      allusersEmail.length > 0 ? keyMapper(allusersEmail, 'Email') : [];
    const ValidationMessage = validateRequiredInputField(
      inputValue,
      t,
      allUsersEmailID
    );
    setInputValidationMessage({
      ...inputValidationMessage,
      [key]: ValidationMessage,
    });
  };
  //To handle the onChange functionality of drop-down
  const handleDropdownChange = (event, value, label) => {
    setUserData({
      ...userData,
      [value]: event && event.value ? event.value.toString() : '',
      [label]: event && event.label ? event.label.toString() : '',
    });
  };

  //To handle the onChange functionality of checkboxes
  const handleCheckboxChange = (event, key) => {
    setUserData({
      ...userData,
      [key]: event.target.checked,
    });
  };

  return (
    <Grid>
      {!isEmptyObject(userDataForEdit) || addOrEdit === 'Add' ? (
        <Modal
          open={isUserModalOpen}
          onClose={onCloseUserModalClick} // Callback fired when the component requests to be closed. Reason Can be: "escapeKeyDown", "backdropClick"
        >
          <Box style={EditAddUserModal}>
            <Typography
              id="modal-title"
              variant="h6"
              component="h2"
              style={UserModalHeading}
            >
              {addOrEdit === 'Add' ? t('Add_User') : t('Edit_User')}
            </Typography>
            <Grid style={UserModelGrid}>
              <Grid style={UserModalLabel}>{`${t('First_Name')}:*`}</Grid>
              <Grid style={UserModelValue}>
                <InputTextField
                  name={'FirstName'}
                  placeholder={t('Enter_First_Name')}
                  style={{
                    borderRadius: '5px',
                  }}
                  value={(userData && userData.FirstName) || ''}
                  onInput={event => handleOnTextChange(event, 'FirstName')}
                />
                <Grid style={validationError}>
                  {inputValidationMessage.FirstName}
                </Grid>
              </Grid>
            </Grid>
            <Grid style={UserModelGrid}>
              <Grid style={UserModalLabel}>{`${t('Last_Name')}:*`}</Grid>
              <Grid style={UserModelValue}>
                <InputTextField
                  name={'LastName'}
                  placeholder={t('Enter_Last_Name')}
                  style={{
                    borderRadius: '5px',
                  }}
                  value={(userData && userData.LastName) || ''}
                  onInput={event => handleOnTextChange(event, 'LastName')}
                />
                <Grid style={validationError}>
                  {inputValidationMessage.LastName}
                </Grid>
              </Grid>
            </Grid>
            <Grid
              sx={{
                margin: '0px 10px 5px 220px',
                overflowWrap: 'break-word',
              }}
            ></Grid>
            <Grid style={UserModelGrid}>
              <Grid style={UserModalLabel}>{`${t('Email')}:*`}</Grid>
              <Grid style={UserModelValue}>
                <InputTextField
                  name={'Email'}
                  placeholder={t('Enter_Email')}
                  style={{
                    borderRadius: '5px',
                  }}
                  value={(userData && userData.Email) || ''}
                  onInput={event => handleOnTextChange(event, 'Email')}
                />
                <Grid style={validationError}>
                  {inputValidationMessage.Email}
                </Grid>
              </Grid>
            </Grid>
            <Grid style={UserModelGrid}>
              <Grid style={UserModalLabel}>{`${t('Site')}:*`}</Grid>
              <Grid style={UserModelValue}>
                <CustomSelect
                  name={'SiteID'}
                  id={'SiteID'}
                  placeholder={t('Select_Site')}
                  options={siteOption}
                  styles={customStyle}
                  value={siteValue}
                  onChange={event =>
                    handleDropdownChange(event, 'SiteID', 'SiteName')
                  }
                />
              </Grid>
            </Grid>
            <Grid style={UserModelGrid}>
              <Grid style={UserModalLabel}>{t('Department')}:</Grid>
              <Grid style={UserModelValue}>
                <CustomSelect
                  name={'DepartmentID'}
                  id={'DepartmentID'}
                  placeholder={t('Select_Department')}
                  options={departmentOption}
                  styles={customStyle}
                  value={departmentValue}
                  isClearable={true}
                  onChange={event =>
                    handleDropdownChange(
                      event,
                      'DepartmentID',
                      'DepartmentName'
                    )
                  }
                />
              </Grid>
            </Grid>
            <Grid style={UserModelGrid}>
              <Grid style={UserModalLabel}>{t('Department_Lead')}:</Grid>
              <Grid style={UserModelValue}>
                <Checkbox
                  style={{ position: 'relative', bottom: '6px' }}
                  checked={(userData && userData.DepartmentLead) || ''}
                  onChange={event =>
                    handleCheckboxChange(event, 'DepartmentLead')
                  }
                />
              </Grid>
            </Grid>
            {addOrEdit === 'Edit' && (
              <Grid style={UserModelGrid}>
                <Grid style={UserModalLabel}>{t('Active')}:</Grid>
                <Grid style={UserModelValue}>
                  <Checkbox
                    style={{ position: 'relative', bottom: '6px' }}
                    checked={(userData && userData.Active) || ''}
                    onChange={event => handleCheckboxChange(event, 'Active')}
                  />
                </Grid>
              </Grid>
            )}
            <Grid style={saveModalButtonGrid}>
              <CustomButton
                title={t('Cancel')}
                onClick={onCloseUserModalClick}
              />
              <CustomButton
                title={t('Save')}
                onClick={() => onSaveUserModalClick(userData)}
                disabled={
                  userData.SiteName === '' ||
                  userData.Email === '' ||
                  userData.LastName === '' ||
                  userData.FirstName === '' ||
                  !hasNotValidationError
                }
              />
            </Grid>
          </Box>
        </Modal>
      ) : (
        <Grid style={EditAddUserModalLoader}>
          <CircularProgress />
        </Grid>
      )}
    </Grid>
  );
};

export default AddEditUserModal;
